package com.shreyasjoshi.models

data class PersonalInfo(
    val name: String,
    val headline: String,
    val specialization: String,
    val summaryParagraphs: List<String>,
    val email: String,
    val phone: String,
    val linkedin: String,
    val knowledgeBase: String,
    val location: String,
    val totalExperience: String,
    val androidExperience: String,
    val domain: String
)

data class ExperienceHighlight(
    val category: String,
    val description: String
)

data class ExperienceItem(
    val id: String,
    val company: String,
    val role: String,
    val employmentType: String,
    val location: String,
    val period: String,
    val duration: String,
    val deploymentNote: String? = null,
    val category: String, // "android" or "it-support"
    val highlights: List<ExperienceHighlight>,
    val technologies: List<String>,
    val projects: List<String> = emptyList()
)

data class ProjectItem(
    val id: String,
    val title: String,
    val clientOrCompany: String,
    val type: String,
    val period: String,
    val summary: String,
    val contributions: List<String>,
    val technicalAreas: List<String>,
    val technologies: List<String>,
    val isProprietary: Boolean = true
)

data class SkillItem(
    val name: String,
    val highlight: Boolean = false
)

data class SkillCategory(
    val title: String,
    val description: String,
    val skills: List<SkillItem>
)

data class EducationItem(
    val degree: String,
    val institution: String,
    val location: String,
    val period: String,
    val score: String,
    val details: String
)

data class InterestCategory(
    val category: String,
    val items: List<String>
)

data class SiteConfig(
    val siteUrl: String = "https://shreyasjoshi.in",
    val canonicalUrl: String = "https://shreyasjoshi.in/",
    val knowledgeBaseUrl: String = "https://notes.shreyasjoshi.in/",
    val title: String = "Shreyas Joshi — Senior Android Developer",
    val description: String = "Senior Android Developer specializing in Android Automotive OS, AOSP, Android HMI, Kotlin, and Android TV."
)

object PortfolioData {
    val config = SiteConfig()

    val personalInfo = PersonalInfo(
        name = "Shreyas Joshi",
        headline = "Senior Android Developer",
        specialization = "Android Automotive • AOSP • Kotlin • Android TV",
        summaryParagraphs = listOf(
            "Senior Android Developer with 4 years of dedicated Android engineering and 7.5 years of comprehensive IT industry experience, specializing in Android Automotive OS (AAOS), AOSP system component integration, and Android HMI development.",
            "Currently engineering next-generation in-vehicle infotainment HMI and Software-Defined Vehicle (SDV) applications at Renault Group. Extensive background spanning AOSP bug resolution in CarSystemUI/CarLauncher, Cuttlefish virtualized testing, ASPICE traceability (SWE.1/SWE.2) with Codebeamer, and formal UML software architecture documentation.",
            "Proven track record of technical evolution—progressing from mission-critical enterprise systems support to high-scale Android TV OTT applications for millions of viewers at Tata Elxsi, and advancing into embedded automotive Android systems."
        ),
        email = "sv10joshi@gmail.com",
        phone = "+91-96650-52537",
        linkedin = "https://linkedin.com/in/sv10joshi",
        knowledgeBase = "https://notes.shreyasjoshi.in/",
        location = "Bengaluru, India",
        totalExperience = "7.5 Years",
        androidExperience = "4 Years",
        domain = "notes.shreyasjoshi.in"
    )

    val experiences = listOf(
        ExperienceItem(
            id = "renault-group",
            company = "Renault Group",
            role = "Senior Android HMI Developer",
            employmentType = "On-site",
            location = "Bengaluru, India",
            period = "Aug 2023 - Present",
            duration = "3 Years",
            category = "android",
            highlights = listOf(
                ExperienceHighlight(
                    category = "UI Implementation",
                    description = "Developed Android HMI screens based on Figma and Outlinr design specifications for in-vehicle infotainment displays."
                ),
                ExperienceHighlight(
                    category = "Code Coverage & Quality",
                    description = "Wrote comprehensive unit and integration tests using MockK and JUnit to continuously improve code coverage and stability."
                ),
                ExperienceHighlight(
                    category = "DevOps & CI/CD",
                    description = "Integrated GitLab CI/CD pipeline jobs for Codebeamer integration using YAML configurations to automate test execution and tracking."
                ),
                ExperienceHighlight(
                    category = "Requirements Traceability",
                    description = "Created and maintained Software Requirements Specifications (SRS / SWE.1), Software Component Requirements (SCRS / SWE.2), test sets, and test cases in collaboration with Product Owners using Codebeamer and Jira."
                ),
                ExperienceHighlight(
                    category = "Software Architecture Documentation",
                    description = "Authored and maintained UML component, sequence, class, and use-case diagrams using PlantUML and MagicDraw to document automotive architectures."
                ),
                ExperienceHighlight(
                    category = "Training & Knowledge Sharing",
                    description = "Created Confluence technical documentation and conducted hands-on training on AOSP repo synchronization, Cuttlefish virtual environment, SDV Test Harness, CI/CD, and Codebeamer environment setup for new joiners."
                ),
                ExperienceHighlight(
                    category = "AOSP Bug Investigation & Fixes",
                    description = "Investigated and resolved deep-seated issues across AOSP CarSystemUI, CarLauncher, and CarMessenger system services."
                )
            ),
            technologies = listOf(
                "AOSP", "Android Automotive OS", "Kotlin", "Java", "Cuttlefish",
                "Codebeamer", "PlantUML", "MagicDraw", "GitLab CI/CD", "MockK", "JUnit", "Figma"
            ),
            projects = listOf("DriveAssist", "ParkAssist", "CarSystemUpdate (SDV)", "CarMaintenance (SDV)")
        ),
        ExperienceItem(
            id = "tata-elxsi",
            company = "Tata Elxsi Limited",
            role = "Android TV Developer",
            employmentType = "On-site",
            location = "Bengaluru, India",
            period = "Jun 2022 - May 2023",
            duration = "1 Year",
            deploymentNote = "Deployed through Quess IT Staffing",
            category = "android",
            highlights = listOf(
                ExperienceHighlight(
                    category = "UI Implementation",
                    description = "Developed and revamped 50+ Android TV screens for MBC, a Dubai-based multilingual OTT launcher application."
                ),
                ExperienceHighlight(
                    category = "Feature Development",
                    description = "Implemented geo-blocking and VPN detection flows to display appropriate blocker and region-specific messages based on device location and network conditions."
                ),
                ExperienceHighlight(
                    category = "API Integration",
                    description = "Integrated and validated REST APIs using Retrofit and Postman, architecting with Dagger 2 and Glide within the existing modular application architecture."
                ),
                ExperienceHighlight(
                    category = "Product Variants",
                    description = "Segregated application functionalities using Gradle product flavors to support both OTT Box and Hybrid Box hardware variants seamlessly."
                ),
                ExperienceHighlight(
                    category = "Cross-Functional Collaboration",
                    description = "Collaborated closely with Design, QA, Backend, and DevOps teams to resolve complex cross-team dependencies and release blockers."
                )
            ),
            technologies = listOf(
                "Android TV", "Kotlin", "Java", "Retrofit", "Dagger 2",
                "Glide", "Postman", "Gradle Product Flavors", "REST APIs", "Git"
            ),
            projects = listOf("MBC Multilingual OTT Launcher (Dubai)")
        ),
        ExperienceItem(
            id = "reliance-industries",
            company = "Reliance Industries Limited",
            role = "Application Support Engineer",
            employmentType = "Remote",
            location = "Mumbai, India",
            period = "Dec 2020 - Apr 2022",
            duration = "1.5 Years",
            deploymentNote = "Deployed through CMS IT Services Pvt. Ltd.",
            category = "it-support",
            highlights = listOf(
                ExperienceHighlight(
                    category = "Technical Troubleshooting",
                    description = "Diagnosed and resolved diverse software, OS, enterprise application, and device issues, establishing a systematic approach to root-cause analysis."
                ),
                ExperienceHighlight(
                    category = "Knowledge Sharing",
                    description = "Authored reusable troubleshooting playbooks and trained onboarding engineers, accelerating resolution time for recurring support tickets."
                )
            ),
            technologies = listOf("Linux", "Enterprise Applications", "OS Diagnostics", "Root Cause Analysis", "Ticketing Systems")
        ),
        ExperienceItem(
            id = "yes-bank",
            company = "Yes Bank Limited",
            role = "IT Support Engineer",
            employmentType = "On-site",
            location = "Mumbai, India",
            period = "Oct 2018 - Dec 2020",
            duration = "2 Years",
            deploymentNote = "Deployed through Wipro Limited",
            category = "it-support",
            highlights = listOf(
                ExperienceHighlight(
                    category = "Process Automation & Optimization",
                    description = "Automated and optimized a recurring CTS scanner troubleshooting process using Windows batch scripting, reducing resolution time from 2–3 hours to approximately 30 minutes."
                ),
                ExperienceHighlight(
                    category = "System Support",
                    description = "Delivered on-site hardware and software infrastructure support across banking operations."
                )
            ),
            technologies = listOf("Windows Batch Scripting", "System Optimization", "Automation", "Hardware Diagnostics")
        )
    )

    val projects = listOf(
        ProjectItem(
            id = "drive-assist",
            title = "DriveAssist",
            clientOrCompany = "Renault Group",
            type = "Proprietary / Automotive",
            period = "2023 - Present",
            summary = "In-vehicle driver assistance HMI application delivering real-time telemetry, visual alerts, and lane-guidance controls directly to the automotive digital cockpit.",
            contributions = listOf(
                "Implemented high-responsiveness HMI views matching exact Figma & Outlinr design specs.",
                "Authored PlantUML sequence and class diagrams for component interaction modeling.",
                "Created ASPICE SWE.1 / SWE.2 software requirements and test specifications in Codebeamer.",
                "Conducted end-to-end verification in Cuttlefish virtualized Android Automotive OS."
            ),
            technicalAreas = listOf("Android Automotive OS", "AOSP", "HMI", "Cuttlefish", "SWE.1 / SWE.2 Traceability"),
            technologies = listOf("Kotlin", "AAOS", "PlantUML", "Codebeamer", "Cuttlefish", "MockK"),
            isProprietary = true
        ),
        ProjectItem(
            id = "park-assist",
            title = "ParkAssist",
            clientOrCompany = "Renault Group",
            type = "Proprietary / Automotive",
            period = "2023 - Present",
            summary = "Automotive parking assistance system interface handling sensor visualization, dynamic trajectory rendering, and obstacle warning states.",
            contributions = listOf(
                "Engineered tactile and visual HMI feedback mechanisms for precise parking maneuvers.",
                "Authored comprehensive unit and integration tests achieving elevated code coverage.",
                "Maintained requirement-to-code traceability within Codebeamer and Jira.",
                "Collaborated with Product Owners on functional safety and UX validation."
            ),
            technicalAreas = listOf("Android HMI", "Automotive UI", "Testing & Verification", "Traceability"),
            technologies = listOf("Kotlin", "Android SDK", "JUnit", "MockK", "Outlinr", "MagicDraw"),
            isProprietary = true
        ),
        ProjectItem(
            id = "car-system-update",
            title = "CarSystemUpdate (SDV)",
            clientOrCompany = "Renault Group",
            type = "Proprietary / Automotive",
            period = "2023 - Present",
            summary = "Software-Defined Vehicle (SDV) over-the-air update management interface ensuring transparent system update stages and failure recovery flows.",
            contributions = listOf(
                "Investigated and resolved AOSP CarSystemUI notification and state sync issues during update routines.",
                "Integrated GitLab CI/CD automated test reporting into Codebeamer.",
                "Documented architecture flows using UML component and state-machine diagrams.",
                "Facilitated SDV Test Harness training sessions for new engineering joiners."
            ),
            technicalAreas = listOf("Software-Defined Vehicle", "AOSP CarSystemUI", "CI/CD DevOps", "Architecture"),
            technologies = listOf("Kotlin", "AOSP", "GitLab CI/CD", "CarSystemUI", "YAML", "SDV Harness"),
            isProprietary = true
        ),
        ProjectItem(
            id = "car-maintenance",
            title = "CarMaintenance (SDV)",
            clientOrCompany = "Renault Group",
            type = "Proprietary / Automotive",
            period = "2023 - Present",
            summary = "Predictive maintenance and diagnostic HMI module communicating vehicle health metrics, service intervals, and diagnostic trouble alerts.",
            contributions = listOf(
                "Architected modular UI components with clean separation of presentation and domain layers.",
                "Resolved lifecycle and inter-process communication bugs in CarLauncher and CarMessenger.",
                "Maintained rigorous SWE.1 software requirements and verification sets.",
                "Created Confluence documentation on repo synchronization and development workflows."
            ),
            technicalAreas = listOf("Vehicle Diagnostics", "CarLauncher", "CarMessenger", "AOSP"),
            technologies = listOf("Kotlin", "AOSP", "CarLauncher", "CarMessenger", "PlantUML", "Codebeamer"),
            isProprietary = true
        ),
        ProjectItem(
            id = "mbc-ott-launcher",
            title = "MBC Multilingual OTT Launcher",
            clientOrCompany = "Tata Elxsi Limited (for MBC Group, Dubai)",
            type = "Proprietary / Android TV",
            period = "2022 - 2023",
            summary = "High-scale Android TV launcher application serving millions of regional and international viewers across the Middle East with multilingual support.",
            contributions = listOf(
                "Developed and revamped 50+ custom Android TV UI screens optimized for D-pad navigation.",
                "Implemented geo-blocking and VPN detection flows ensuring content licensing compliance.",
                "Integrated REST APIs via Retrofit with Dagger 2 dependency injection and Glide image loading.",
                "Configured Gradle product flavors to support OTT Box and Hybrid Box deployment variants."
            ),
            technicalAreas = listOf("Android TV", "D-Pad Navigation", "Geo-blocking", "Multilingual OTT", "Product Flavors"),
            technologies = listOf("Kotlin", "Java", "Android TV SDK", "Retrofit", "Dagger 2", "Glide", "Gradle Flavors"),
            isProprietary = true
        )
    )

    val skillCategories = listOf(
        SkillCategory(
            title = "Languages",
            description = "Core programming languages utilized in production embedded, automotive, and Android applications.",
            skills = listOf(
                SkillItem("Kotlin", highlight = true),
                SkillItem("Java", highlight = true),
                SkillItem("PlantUML (Architecture DSL)"),
                SkillItem("Windows Batch / Shell")
            )
        ),
        SkillCategory(
            title = "Platforms & Systems",
            description = "Operating systems, target platforms, and AOSP system service layers.",
            skills = listOf(
                SkillItem("AOSP (Android Open Source Project)", highlight = true),
                SkillItem("Android Automotive OS (AAOS)", highlight = true),
                SkillItem("Android TV", highlight = true),
                SkillItem("Android SDK", highlight = true),
                SkillItem("Linux", highlight = true)
            )
        ),
        SkillCategory(
            title = "Android Architecture & Frameworks",
            description = "Modern reactive asynchronous paradigms, dependency injection, networking, and testing.",
            skills = listOf(
                SkillItem("Kotlin Coroutines", highlight = true),
                SkillItem("Kotlin Flow", highlight = true),
                SkillItem("ViewModel & Jetpack Architecture", highlight = true),
                SkillItem("Android UI / HMI Development", highlight = true),
                SkillItem("Retrofit & REST APIs", highlight = true),
                SkillItem("Dagger 2", highlight = true),
                SkillItem("MockK & JUnit", highlight = true),
                SkillItem("Glide"),
                SkillItem("Gradle Product Flavors")
            )
        ),
        SkillCategory(
            title = "Automotive & Virtualization",
            description = "Specialized tools for in-vehicle simulation, AOSP system UI, and vehicle software testing.",
            skills = listOf(
                SkillItem("Cuttlefish Virtual Device", highlight = true),
                SkillItem("CarSystemUI", highlight = true),
                SkillItem("CarLauncher", highlight = true),
                SkillItem("CarMessenger", highlight = true),
                SkillItem("SDV Test Harness", highlight = true),
                SkillItem("AOSP Repo Synchronization")
            )
        ),
        SkillCategory(
            title = "Engineering, Process & Modeling Tools",
            description = "Enterprise software modeling, traceability, version control, and CI/CD pipelines.",
            skills = listOf(
                SkillItem("Git & GitLab CI/CD", highlight = true),
                SkillItem("Codebeamer (ASPICE SWE.1 / SWE.2)", highlight = true),
                SkillItem("Jira & Confluence", highlight = true),
                SkillItem("PlantUML", highlight = true),
                SkillItem("MagicDraw UML", highlight = true),
                SkillItem("Postman"),
                SkillItem("Figma & Outlinr")
            )
        )
    )

    val education = listOf(
        EducationItem(
            degree = "Bachelor of Engineering (Computer Science)",
            institution = "University of Mumbai",
            location = "Mumbai, India",
            period = "Jun 2012 - May 2016",
            score = "CGPI: 7.01 / 10",
            details = "Comprehensive coursework in Operating Systems, Computer Networks, Data Structures, Algorithms, Distributed Computing, and Object-Oriented Software Engineering."
        )
    )

    val interests = listOf(
        InterestCategory(
            category = "Fitness & Wellness",
            items = listOf("Yoga", "Swimming", "Cycling")
        ),
        InterestCategory(
            category = "Music",
            items = listOf("Harmonica", "Djembe (West African Percussion)")
        )
    )
}

